import React, { useEffect, useRef, useState } from "react";
import { Plus, SlidersHorizontal, LogIn, ChevronDown, Search } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Badge } from "@pgs-core/ui/components/badge";
import { Input } from "@pgs-core/ui/components/input";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@pgs-core/ui/components/table";
import { PaginationControls } from "@pgs-core/ui/components/pagination-controls";
import { CLIENT_PORTAL_NAV } from "@pgs-core/portal-lib/client-portal-nav";
import type { ListControls } from "@pgs-core/portal-lib/list-controls";
import type { AdminClient } from "../lib/outlet-context";
import { PLAN_LABELS } from "../lib/plans";

function lifecycleBadgeVariant(lifecycle: string) {
  const normalized = lifecycle.toLowerCase();
  if (["live", "active"].includes(normalized)) return "success";
  if (["provisioning", "draft"].includes(normalized)) return "warning";
  return "secondary";
}

// A tenant whose provisioned DB lags the latest core schema version.
function isSchemaOutOfDate(client: AdminClient, schemaLatest: number) {
  return Boolean(client.d1_database_id) && schemaLatest > 0 && (client.schema_version ?? 0) < schemaLatest;
}

/**
 * The "Portal" split button: the main half opens the client's portal on the
 * Dashboard as before; the chevron half lists the other pages in the Client
 * Portal's navigation. The menu is position:fixed so it escapes the table's
 * overflow-auto wrapper instead of scrolling inside it.
 */
function PortalMenuButton({ disabled, title, onOpen }: {
  disabled: boolean;
  title: string;
  onOpen: (path: string) => void;
}) {
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuPos) return;
    const close = () => setMenuPos(null);
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // The menu is fixed-position: close rather than drift when the page scrolls.
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
    };
  }, [menuPos]);

  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (menuPos) {
      setMenuPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  return (
    <div ref={ref} className="flex items-center">
      <Button
        variant="ghost"
        size="sm"
        className="rounded-r-none pr-2"
        onClick={() => onOpen("/")}
        disabled={disabled}
        title={title}
      >
        <LogIn className="h-4 w-4" />
        Portal
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="rounded-l-none border-l px-1"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(menuPos)}
        aria-label="Open portal page menu"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {menuPos && (
        <div
          role="menu"
          className="fixed z-50 min-w-36 rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          {CLIENT_PORTAL_NAV.map((page) => (
            <button
              key={page.href}
              type="button"
              role="menuitem"
              className="flex w-full items-center px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setMenuPos(null);
                onOpen(page.href);
              }}
            >
              {page.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientsPanel({
  clients, loaded, error, total, controls,
  onViewStatus, onNewClient, onOpenPortal, canOpenPortal, portalTitle, schemaLatest = 0,
}: {
  clients: AdminClient[];
  /** False until the first page has arrived (avoids an empty-state flash). */
  loaded: boolean;
  error: string;
  total: number;
  controls: ListControls;
  onViewStatus: (clientId: string) => void;
  onNewClient: () => void;
  onOpenPortal: (client: AdminClient, path?: string) => void;
  canOpenPortal: (client: AdminClient) => boolean;
  portalTitle: (client: AdminClient) => string;
  schemaLatest?: number;
}) {
  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
        <Button size="sm" onClick={onNewClient}>
          <Plus className="h-4 w-4" />
          New client
        </Button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={controls.search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
          placeholder="Search clients…"
          className="pl-8"
          aria-label="Search clients"
        />
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      {/* Empty state only when there is truly nothing to page to — an empty
          window on a stale page keeps the table + footer so the clamp in
          PaginationControls can snap back to the last real page. */}
      {loaded && clients.length === 0 && total === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {controls.q ? "No clients match your search" : "No clients yet"}
          </p>
          {!controls.q && (
            <p className="mt-1 text-xs text-muted-foreground">
              Use the Provision tab to create your first client.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>
                    <div className="font-medium">{client.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {client.custom_domain ??
                        (client.pages_project_name ? `${client.pages_project_name}.pages.dev` : "Not provisioned")}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">{client.id}</code>
                  </TableCell>
                  <TableCell>{(client.plan && PLAN_LABELS[client.plan]) || client.plan}</TableCell>
                  <TableCell className="capitalize">{client.billingStatus}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge variant={lifecycleBadgeVariant(client.lifecycle ?? "")}>{client.lifecycle}</Badge>
                      {isSchemaOutOfDate(client, schemaLatest) && (
                        <Badge variant="warning" title="Database schema is out of date">DB update</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => onViewStatus(client.id)}>
                        <SlidersHorizontal className="h-4 w-4" />
                        Manage
                      </Button>
                      <PortalMenuButton
                        disabled={!canOpenPortal(client)}
                        title={portalTitle(client)}
                        onOpen={(path) => onOpenPortal(client, path)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationControls
            page={controls.page}
            total={total}
            perPage={controls.perPage}
            onPageChange={controls.setPage}
            onPerPageChange={controls.setPerPage}
            itemsLabel="clients"
            className="border-t px-4 py-3"
          />
        </div>
      )}
    </div>
  );
}
